import type {
	FoodAnalysisResponse,
	FoodRating,
	ObjectCategory,
	WeightImpact,
} from "../domains/food-analysis/food-analysis.model";

const ratingLabels: Record<FoodRating, string> = {
	avoid: "Nên tránh",
	limit: "Nên hạn chế",
	usually_ok: "Thường phù hợp",
	uncertain: "Chưa chắc chắn",
};

const categoryLabels: Record<ObjectCategory, string> = {
	food: "Món ăn",
	vegetable: "Rau củ",
	fruit: "Trái cây",
	drink: "Đồ uống",
	medicine: "Thuốc",
	supplement: "Thực phẩm bổ sung",
	other: "Khác",
};
const weightLabels: Record<WeightImpact, string> = {
	helpful: "Hỗ trợ cân nặng",
	neutral: "Trung tính cân nặng",
	risk: "Dễ tăng cân",
	uncertain: "Chưa rõ cân nặng",
};

interface AnalysisResultPanelProps {
	loading: boolean;
	result: FoodAnalysisResponse | null;
	onReset: () => void;
}

export function AnalysisResultPanel({
	loading,
	result,
	onReset,
}: AnalysisResultPanelProps) {
	return (
		<aside className="result-card" aria-live="polite">
			<div className="step">
				<span>02</span>
				<div>
					<strong>Kết quả phân tích</strong>
					<small>Đánh giá gout và béo phì cho từng đối tượng</small>
				</div>
			</div>

			{loading ? (
				<div className="loading-state">
					<span className="spinner" />
					<strong>Đang phân tích các đối tượng</strong>
					<small>Nhận diện từng đối tượng → đánh giá gout và cân nặng</small>
				</div>
			) : result ? (
				<div className="result-content">
                    <strong>{result.results.length} đối tượng được nhận diện</strong>
                    {result.truncated && <p role="status">Ảnh có nhiều đối tượng; chỉ hiển thị tối đa 10 đối tượng. Hãy chụp các đối tượng còn lại trong ảnh khác.</p>}
                    {result.results.length === 0 && <p>Chưa nhận diện được đối tượng nào. Hãy chụp lại rõ hơn trong điều kiện đủ sáng.</p>}
                    {result.results.map((result, index) => (
                    <article className="object-result" key={result.id} aria-label={`Đối tượng ${index + 1}: ${result.objectName}`}>
                    {result.assessmentStatus === "failed" && <p role="status">Đánh giá chưa hoàn tất — hãy thử lại đối tượng này.</p>}
					<div className="identified">
						<div>
							<small>
								{categoryLabels[result.category] ?? result.category} • AI nhận
								diện
							</small>
							<h2>{index + 1}. {result.objectName}</h2>
						</div>
						<span>{Math.round(result.confidence * 100)}% chắc chắn</span>
					</div>
					<div className={`rating ${result.rating}`}>
						<small>Đối với người bị gout</small>
						<strong>{ratingLabels[result.rating]}</strong>
						<p>{result.summary}</p>
					</div>
					{result.weightImpact && (
						<div className={`rating weight ${result.weightImpact}`}>
							<small>Đối với thừa cân / béo phì</small>
							<strong>
									{weightLabels[result.weightImpact as WeightImpact] ??
										result.weightImpact}
								</strong>
							<p>{result.overweightSummary ?? "Chưa có thông tin chi tiết về cân nặng."}</p>
							{result.caloriesEstimate && <small>{result.caloriesEstimate}</small>}
						</div>
					)}
					{result.sources && result.sources.length > 0 && (
						<div className="detail-block sources">
							<h3>Nguồn tham khảo</h3>
							<ul>
								{result.sources.map((s) => (
									<li key={s.url}>
										<a href={s.url} target="_blank" rel="noopener noreferrer">
											{s.title}
										</a>{" "}
										<small>{s.url}</small>
									</li>
								))}
							</ul>
						</div>
					)}
					<div className="detail-block">
						<h3>Vì sao?</h3>
						<ul>
							{result.reasons.map((reason) => (
								<li key={reason}>{reason}</li>
							))}
						</ul>
					</div>
					<div className="detail-block tips">
						<h3>Gợi ý thực tế</h3>
						<ul>
							{result.tips.map((tip) => (
								<li key={tip}>{tip}</li>
							))}
						</ul>
					</div>
                    </article>
                    ))}
					<button type="button" className="scan-again" onClick={onReset}>
						Quét ảnh khác
					</button>
				</div>
			) : (
				<div className="placeholder-result">
					<div className="plate">
						<span>✦</span>
					</div>
					<strong>Kết quả sẽ xuất hiện tại đây</strong>
					<p>
						AI nhận diện các đối tượng trong ảnh, sau đó đánh giá riêng từng đối tượng
                        về mức độ phù hợp với gout và tác động tới thừa cân / béo phì.
					</p>
					<div className="legend">
						<span className="ok">Thường phù hợp</span>
						<span className="limit">Hạn chế</span>
						<span className="avoid">Nên tránh</span>
					</div>
				</div>
			)}
		</aside>
	);
}

